import { Resources } from 'constants/resources';

import { Fragment, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { GatewayRuleMappingDescriptionEditDialog } from 'pages/GatewayRules/components/GatewayRuleMappingDescriptionDialog/GatewayRuleMappingDescriptionEditDialog';
import { useMappingItemField } from 'pages/GatewayRules/components/GatewayRulesForm/hooks';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetOne } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Button } from 'shared/mui/Buttons';
import { Card } from 'shared/mui/Card';
import { Divider } from 'shared/mui/Divider/Divider';
import { Method } from 'types/embedded/method';

import { GatewayRulesFormMappingHead } from './GatewayRulesFormMappingHead';
import { GatewayRulesFormMappingItem } from './GatewayRulesFormMappingItem';
import { useAutoSelectDirection } from './hooks/useAutoSelectDirection';
import { useGatewayRulesMerchantDictionaryContext } from '../context/GatewayRulesMerchantDictionaryContext';
import { useMappingGateways } from './hooks/useMappingGateways';

type Props = {
  mappingIndex: number;
  onRemoveMappingClick: (index: number) => void;
  onChangeMethod: (index: number) => void;
  onChangeDirection: (index: number) => void;
};

export function GatewayRulesFormMapping({
  mappingIndex,
  onChangeMethod,
  onChangeDirection,
  onRemoveMappingClick,
}: Props): JSX.Element {
  const translate = useTypedTranslate();
  const { getValues, watch, setValue } = useFormContext();
  const direction = watch(`mappings.${mappingIndex}.direction`);

  const { enableAdditionalFields } = useGatewayRulesMerchantDictionaryContext();
  const { gateways, isLoadingGateways } = useMappingGateways(mappingIndex);

  useAutoSelectDirection({ mappingIndex, onChangeDirection });

  const { mappingItemFields, onRemoveMappingItemClick, onAddMappingItemClick } =
    useMappingItemField({
      mappingIndex,
      name: `mappings.${mappingIndex}.mappingItems`,
    });

  const [editDescription, setEditDescription] = useState(false);

  const { data: method, isLoading: isLoadingMethod } = useGetOne<Method>(
    Resources.Methods,
    {
      id: getValues(`mappings.${mappingIndex}.method`),
    },
    { enabled: !!getValues(`mappings.${mappingIndex}.method`) },
  );

  const onDescriptionSubmit = (v: string) => {
    setValue(`mappings.${mappingIndex}.description`, v, { shouldDirty: true });
  };
  return (
    <Card
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.08)',
      }}
    >
      <GatewayRulesFormMappingHead
        enableAdditionalFields={enableAdditionalFields}
        isLoadingMethod={isLoadingMethod}
        mappingIndex={mappingIndex}
        methodLogo={method?.logo}
        onChangeDirection={onChangeDirection}
        onChangeMethod={onChangeMethod}
        onEditDescriptionClick={() => setEditDescription(true)}
        onRemoveMappingClick={onRemoveMappingClick}
      />
      {mappingItemFields.map((mappingItem, mappingItemIndex) => (
        <Fragment key={mappingItem.id}>
          <GatewayRulesFormMappingItem
            direction={direction}
            enableAdditionalFields={enableAdditionalFields}
            gateways={gateways}
            isLoadingGateways={isLoadingGateways}
            key={mappingItem.id}
            mappingIndex={mappingIndex}
            mappingItemIndex={mappingItemIndex}
            onRemoveMappingItemClick={onRemoveMappingItemClick}
          />
          {mappingItemIndex !== mappingItemFields?.length - 1 && (
            <Divider orientation="horizontal" sx={{ marginX: '24px', marginBottom: '16px' }} />
          )}
        </Fragment>
      ))}
      <Button
        label={translate('app.gatewayRules.addGateway')}
        onClick={onAddMappingItemClick}
        startIcon={<AddIcon />}
        sx={{ marginBottom: '24px', marginLeft: '24px' }}
        variant="outlined"
      />
      {editDescription && (
        <GatewayRuleMappingDescriptionEditDialog
          mapping={getValues(`mappings.${mappingIndex}`)}
          methodName={method?.name}
          onClose={() => setEditDescription(false)}
          onSubmitDialog={onDescriptionSubmit}
          open={editDescription}
        />
      )}
    </Card>
  );
}
